import '@angular/localize/init';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, Inject, Input, NgModule, TemplateRef, Component, HostBinding, ContentChildren, ViewChild, forwardRef, Injectable, Pipe } from '@angular/core';
import * as i2 from '@ng-bootstrap/ng-bootstrap';
import { NgbTooltip, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, BehaviorSubject, Subscription, ReplaySubject, combineLatest, startWith, map, of } from 'rxjs';
import { __decorate } from 'tslib';
import * as i1$1 from '@angular/forms';
import { FormArray, Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { filter, map as map$1, shareReplay } from 'rxjs/operators';
import colorLib from '@kurkle/color';
import * as i3 from 'ng2-charts';
import { NgChartsModule } from 'ng2-charts';
import * as i1$2 from '@angular/platform-browser';

/**
 * Different types of the tooltip
 * info: Fast
 * descriptive: Delayed
 */
const TooltipTypeValues = ['info', 'descriptive'];
/** Common config (overwritten by the specific one for the types) */
const COMMON_CONFIG = {
    openDelay: 0,
    closeDelay: 0,
    container: 'body',
    tooltipClass: 'apos-tooltip',
};
/** Configs for the different types (overwrite common config) */
const CONFIG = {
    info: {
        ...COMMON_CONFIG,
        openDelay: 100,
    },
    descriptive: {
        ...COMMON_CONFIG,
        openDelay: 1000,
        closeDelay: 300,
    },
};
// @dynamic
class TooltipDirective extends NgbTooltip {
    /** Tooltip content */
    set aposTooltip(value) {
        this.ngbTooltip = value;
    }
    /** Tooltip type (input setter). Default: 'info' */
    set aposTooltipType(value) {
        if (value === null)
            return;
        /* istanbul ignore if */
        if (!TooltipTypeValues.includes(value))
            throw new Error(`invalid tooltip type: '${value}'`);
        this.type$.next(value);
    }
    constructor(elementRef, renderer, injector, viewContainerRef, config, ngZone, _document, changeDetector, applicationRef) {
        super(elementRef, renderer, injector, viewContainerRef, config, ngZone, _document, changeDetector, applicationRef);
        this.unsubscribe$ = new Subject();
        /** Input Observable of the tooltip type */
        this.type$ = new BehaviorSubject('info');
        this.type$.subscribe(type => this.setConfig(type));
    }
    setConfig(type) {
        Object.assign(this, CONFIG[type]);
        // eslint-disable-next-line no-self-assign
        this.ngbTooltip = this.ngbTooltip; // To trigger setter
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        // Clean up all subscriptions
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.Injector }, { token: i0.ViewContainerRef }, { token: i2.NgbTooltipConfig }, { token: i0.NgZone }, { token: DOCUMENT }, { token: i0.ChangeDetectorRef }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: TooltipDirective, selector: "[aposTooltip]", inputs: { aposTooltip: "aposTooltip", aposTooltipType: "aposTooltipType" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aposTooltip]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.Injector }, { type: i0.ViewContainerRef }, { type: i2.NgbTooltipConfig }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.ApplicationRef }]; }, propDecorators: { aposTooltip: [{
                type: Input
            }], aposTooltipType: [{
                type: Input
            }] } });

class AposTooltipsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposTooltipsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposTooltipsModule, declarations: [TooltipDirective], imports: [CommonModule], exports: [TooltipDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposTooltipsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposTooltipsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TooltipDirective,
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        TooltipDirective,
                    ],
                }]
        }] });

function getComponentProp(t, key) {
    /* istanbul ignore if */
    if (t.hasOwnProperty(key)) {
        return t[key];
    }
    else
        return undefined;
}
/**
  * Adding @AutoUnsubscribe() to your component (just after @Component ) will automatically unsubscribe from all subscribe
  * from all subscribtions that are stored in the subscriptions property of the component instance
  * For example: component.subscriptions = new Subscription(); component.subscriptions.add(xy.subscribe(...))
  * Adapted from (with fallback for services): https://medium.com/@gesteira2046/how-to-auto-unsubscribe-in-angular-9-da7647cc8b54
  */
function AutoUnsubscribe() {
    return (constructor) => {
        const cmp = getComponentProp(constructor, 'ɵcmp') || constructor.prototype; // Ɵcmp, fallback to prototype for all others
        const original = cmp.onDestroy || cmp.ngOnDestroy;
        // This cannot be an arrow function so that we get the correct context of `this`.
        cmp.onDestroy = function (...args) {
            if (this.subscriptions &&
                this.subscriptions.unsubscribe &&
                typeof this.subscriptions.unsubscribe === "function") {
                this.subscriptions.unsubscribe();
            }
            if (original && typeof original === "function") {
                original.apply(this, args); // Call original function
            }
        };
    };
}

let ExpandingMenuBarItemDirective = class ExpandingMenuBarItemDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpandingMenuBarItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: ExpandingMenuBarItemDirective, selector: "[aposExpandingMenuBarItem]", ngImport: i0 }); }
};
ExpandingMenuBarItemDirective = __decorate([
    AutoUnsubscribe()
], ExpandingMenuBarItemDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpandingMenuBarItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aposExpandingMenuBarItem]',
                }]
        }] });

let ExpandingMenuBarComponent = class ExpandingMenuBarComponent {
    constructor(changeDetectorRef, elementRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.elementRef = elementRef;
        this.subscriptions = new Subscription();
        /** List of classes applied to the dropdown trigger button */
        this.overflowTriggerClass = 'btn btn-sm btn-link text-dark link-like text-decoration-none rounded px-2 py-1';
        /** The host element width in px */
        this.hostElementWidth$ = new ReplaySubject(1);
        this.shownElementsCount$ = new BehaviorSubject(0);
        // For use in template
        this.Array = Array;
    }
    ngAfterViewInit() {
        this.resizeObserver = new ResizeObserver(([el]) => {
            this.hostElementWidth$.next(el.contentRect.width);
        });
        this.resizeObserver.observe(this.elementRef.nativeElement);
        this.subscriptions.add(this.shownElementsCount$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
        }));
        /* istanbul ignore next: Direct interaction with DOM is not unit-tested */
        this.subscriptions.add(combineLatest([
            this.hostElementWidth$,
            this.itemEls.changes.pipe(startWith(undefined)),
        ]).pipe(map(([hostElementWidth]) => {
            let counter = 0;
            const allHidden = Array.from(this.hiddenItems.nativeElement.children);
            const hiddenItemEls = allHidden.slice(0, -1);
            const hiddenOverflowButton = allHidden[allHidden.length - 1];
            const overflowButtonWidth = hiddenOverflowButton.offsetWidth;
            const overflowButtonGap = hiddenItemEls.length > 0 ?
                hiddenOverflowButton.offsetLeft - (hiddenItemEls[hiddenItemEls.length - 1].offsetLeft + hiddenItemEls[hiddenItemEls.length - 1].offsetWidth) :
                0; // cannot calculate gap if there is no previous element
            for (const itemEl of hiddenItemEls) {
                const relevantWidth = counter === hiddenItemEls.length - 1 ?
                    itemEl.offsetLeft + itemEl.offsetWidth : // no need to calculate with button if all elements shown
                    itemEl.offsetLeft + itemEl.offsetWidth + overflowButtonGap + overflowButtonWidth;
                if (relevantWidth >= hostElementWidth)
                    break;
                counter++;
            }
            return counter;
        })).subscribe(shownElementsCount => this.shownElementsCount$.next(shownElementsCount)));
    }
    /* istanbul ignore next */
    onOpenOverflowMenu() {
        const overflowEls = Array.from(this.overflowMenuEl.nativeElement.children);
        for (const el of overflowEls)
            el.classList.add('dropdown-item', 'rounded-0');
    }
    ngOnDestroy() {
        this.resizeObserver?.disconnect();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpandingMenuBarComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ExpandingMenuBarComponent, selector: "apos-expanding-menu-bar", inputs: { containerClass: "containerClass", overflowTriggerClass: "overflowTriggerClass" }, host: { properties: { "class": "this.containerClass" } }, queries: [{ propertyName: "itemEls", predicate: ExpandingMenuBarItemDirective, read: TemplateRef }], viewQueries: [{ propertyName: "hiddenItems", first: true, predicate: ["hiddenItems"], descendants: true }, { propertyName: "overflowMenuEl", first: true, predicate: ["overflowMenu"], descendants: true }], ngImport: i0, template: "<!-- Menu bar -->\n<ng-container\n  *ngFor=\"\n    let itemEl of Array.from(itemEls).slice(0, shownElementsCount$ | async)\n  \"\n>\n  <ng-container [ngTemplateOutlet]=\"itemEl\"></ng-container>\n</ng-container>\n\n<!-- Overflow -->\n<div\n  ngbDropdown\n  class=\"position-relative\"\n  [class.d-none]=\"\n    (shownElementsCount$ | async) === null ||\n    (shownElementsCount$ | async) === itemEls.length\n  \"\n  (openChange)=\"$event && onOpenOverflowMenu()\"\n  container=\"body\"\n>\n  <button\n    type=\"button\"\n    class=\"no-dropdown-arrow\"\n    [ngClass]=\"overflowTriggerClass\"\n    ngbDropdownToggle\n  >\n    <span class=\"icon-arrow-down\"></span>\n  </button>\n  <div #overflowMenu ngbDropdownMenu class=\"overflow-menu\">\n    <ng-container\n      *ngFor=\"\n        let itemEl of Array.from(itemEls).slice(shownElementsCount$ | async)\n      \"\n    >\n      <ng-container [ngTemplateOutlet]=\"itemEl\"></ng-container>\n    </ng-container>\n  </div>\n</div>\n\n<!-- Hidden for evaluation -->\n<div\n  #hiddenItems\n  class=\"hidden-items invisible position-absolute\"\n  [ngClass]=\"containerClass\"\n>\n  <ng-container *ngFor=\"let itemEl of itemEls\">\n    <div ngbDropdownItem>\n      <ng-container [ngTemplateOutlet]=\"itemEl\"></ng-container>\n    </div>\n  </ng-container>\n\n  <div ngbDropdown>\n    <button\n      type=\"button\"\n      class=\"no-dropdown-arrow\"\n      [ngClass]=\"overflowTriggerClass\"\n      ngbDropdownToggle\n    >\n      <span class=\"icon-arrow-down\"></span>\n    </button>\n  </div>\n</div>\n", styles: [":host,.hidden-items{position:relative;display:flex;align-items:center;white-space:nowrap;overflow:hidden}.overflow-menu.show{display:flex;flex-direction:column}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i2.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i2.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i2.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["disabled"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
};
ExpandingMenuBarComponent = __decorate([
    AutoUnsubscribe()
], ExpandingMenuBarComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpandingMenuBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-expanding-menu-bar', template: "<!-- Menu bar -->\n<ng-container\n  *ngFor=\"\n    let itemEl of Array.from(itemEls).slice(0, shownElementsCount$ | async)\n  \"\n>\n  <ng-container [ngTemplateOutlet]=\"itemEl\"></ng-container>\n</ng-container>\n\n<!-- Overflow -->\n<div\n  ngbDropdown\n  class=\"position-relative\"\n  [class.d-none]=\"\n    (shownElementsCount$ | async) === null ||\n    (shownElementsCount$ | async) === itemEls.length\n  \"\n  (openChange)=\"$event && onOpenOverflowMenu()\"\n  container=\"body\"\n>\n  <button\n    type=\"button\"\n    class=\"no-dropdown-arrow\"\n    [ngClass]=\"overflowTriggerClass\"\n    ngbDropdownToggle\n  >\n    <span class=\"icon-arrow-down\"></span>\n  </button>\n  <div #overflowMenu ngbDropdownMenu class=\"overflow-menu\">\n    <ng-container\n      *ngFor=\"\n        let itemEl of Array.from(itemEls).slice(shownElementsCount$ | async)\n      \"\n    >\n      <ng-container [ngTemplateOutlet]=\"itemEl\"></ng-container>\n    </ng-container>\n  </div>\n</div>\n\n<!-- Hidden for evaluation -->\n<div\n  #hiddenItems\n  class=\"hidden-items invisible position-absolute\"\n  [ngClass]=\"containerClass\"\n>\n  <ng-container *ngFor=\"let itemEl of itemEls\">\n    <div ngbDropdownItem>\n      <ng-container [ngTemplateOutlet]=\"itemEl\"></ng-container>\n    </div>\n  </ng-container>\n\n  <div ngbDropdown>\n    <button\n      type=\"button\"\n      class=\"no-dropdown-arrow\"\n      [ngClass]=\"overflowTriggerClass\"\n      ngbDropdownToggle\n    >\n      <span class=\"icon-arrow-down\"></span>\n    </button>\n  </div>\n</div>\n", styles: [":host,.hidden-items{position:relative;display:flex;align-items:center;white-space:nowrap;overflow:hidden}.overflow-menu.show{display:flex;flex-direction:column}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }]; }, propDecorators: { containerClass: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class']
            }], overflowTriggerClass: [{
                type: Input
            }], itemEls: [{
                type: ContentChildren,
                args: [ExpandingMenuBarItemDirective, { read: TemplateRef }]
            }], hiddenItems: [{
                type: ViewChild,
                args: ['hiddenItems']
            }], overflowMenuEl: [{
                type: ViewChild,
                args: ['overflowMenu']
            }] } });

let SparklesComponent = class SparklesComponent {
    constructor() {
        /** Bundle for all subscriptions */
        this.subscriptions = new Subscription();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SparklesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SparklesComponent, selector: "apos-sparkles", ngImport: i0, template: "<span class=\"icon-sparkles icon\" [aposTooltip]=\"tooltip\"></span>\n<ng-template #tooltip>\n  <ng-content></ng-content>\n</ng-template>\n", styles: [".icon{color:#fad75d}\n"], dependencies: [{ kind: "directive", type: TooltipDirective, selector: "[aposTooltip]", inputs: ["aposTooltip", "aposTooltipType"] }] }); }
};
SparklesComponent = __decorate([
    AutoUnsubscribe()
], SparklesComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SparklesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-sparkles', template: "<span class=\"icon-sparkles icon\" [aposTooltip]=\"tooltip\"></span>\n<ng-template #tooltip>\n  <ng-content></ng-content>\n</ng-template>\n", styles: [".icon{color:#fad75d}\n"] }]
        }] });

class AposComponentsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposComponentsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposComponentsModule, declarations: [ExpandingMenuBarComponent,
            ExpandingMenuBarItemDirective,
            SparklesComponent], imports: [CommonModule,
            OverlayModule,
            PortalModule,
            AposTooltipsModule,
            NgbModule], exports: [ExpandingMenuBarComponent,
            ExpandingMenuBarItemDirective,
            SparklesComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposComponentsModule, imports: [CommonModule,
            OverlayModule,
            PortalModule,
            AposTooltipsModule,
            NgbModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposComponentsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ExpandingMenuBarComponent,
                        ExpandingMenuBarItemDirective,
                        SparklesComponent,
                    ],
                    imports: [
                        CommonModule,
                        OverlayModule,
                        PortalModule,
                        AposTooltipsModule,
                        NgbModule,
                    ],
                    exports: [
                        ExpandingMenuBarComponent,
                        ExpandingMenuBarItemDirective,
                        SparklesComponent,
                    ],
                }]
        }] });

/**
 * Check if a formArray has >=1 control in it. Possible errors: arrayEmpty
 */
const ArrayNotEmptyValidator = (form) => {
    /* istanbul ignore next */
    if (form.controls.length === 0)
        return { 'arrayEmpty': true };
    return null;
};

/**
 * Check if all the formGroups have a unique value for the specified property. Possible errors: arrayPropertyNotUnique
 */
function ArrayPropertyUniqueValidator(property) {
    return (control) => {
        const controls = control.controls;
        const valueMap = {};
        for (const control of controls) {
            const group = control;
            const valueControl = group.get(property);
            if (!valueControl)
                return { 'arrayPropertyUniqueValidatorInternalError': true };
            const value = valueControl.value;
            if (!valueMap[value])
                valueMap[value] = [group];
            else
                valueMap[value].push(group);
        }
        for (const groups of Object.values(valueMap)) {
            if (groups.length > 1)
                return { 'arrayPropertyNotUnique': true };
        }
        return null;
    };
}

/**
 * A validator for different date formats using moment as underlying library
 */
function DateValidator(format) {
    return (control) => {
        return [null, ''].includes(control.value) || moment(control.value, format).isValid() ?
            null :
            { invalidDate: { value: control.value, format: format } };
    };
}
/**
 * Validate <= maxDate
 */
function DateMaxValidator(format, maxDate) {
    return (control) => {
        return [null, ''].includes(control.value) || moment(control.value, format).isValid() && moment(control.value, format).isSameOrBefore(moment(maxDate, format)) ?
            null :
            { maxDate: { value: control.value, format, maxDate } };
    };
}
/**
 * Validate >= minDate
 */
function DateMinValidator(format, minDate) {
    return (control) => {
        return [null, ''].includes(control.value) || moment(control.value, format).isValid() && moment(control.value, format).isSameOrAfter(moment(minDate, format)) ?
            null :
            { minDate: { value: control.value, format, minDate } };
    };
}

/**
 * Check if a file input has the correct file type based on the file name's ending
 */
function FileEndingValidator(endings) {
    return (control) => {
        if (!control.value)
            return null;
        const name = control.value[0].name;
        if (!name)
            return null;
        if (!endings.some(ending => name.toLowerCase().endsWith(`.${ending}`)))
            return { fileEnding: true };
        return null;
    };
}

/**
 * A validator for an array of checkboxes out of which at least some must be selected
 */
function MinCheckedValidator(min) {
    return (formArray) => {
        if (formArray instanceof FormArray) {
            const totalSelected = formArray.controls
                .map((control) => !!control.value)
                .reduce((prev, next) => next ? prev + 1 : prev, 0);
            return totalSelected >= min ? null : { required: true };
        }
        throw new Error('formArray is not an instance of FormArray');
    };
}

var CronEditorComponent_1;
var VARIANTS;
(function (VARIANTS) {
    VARIANTS[VARIANTS["DAY_OF_WEEK"] = 1] = "DAY_OF_WEEK";
    VARIANTS[VARIANTS["DAY_OF_MONTH"] = 2] = "DAY_OF_MONTH";
    VARIANTS[VARIANTS["LAST_DAY_OF_MONTH"] = 3] = "LAST_DAY_OF_MONTH";
    VARIANTS[VARIANTS["LAST_SPECIFIC_DAY_OF_MONTH"] = 4] = "LAST_SPECIFIC_DAY_OF_MONTH";
})(VARIANTS || (VARIANTS = {}));
const DAYS = [
    $localize `:monday|Monday@@ng-aposphere.cron-editor.monday:Monday`,
    $localize `:tuesday|Tuesday@@ng-aposphere.cron-editor.tuesday:Tuesday`,
    $localize `:wednesday|Wednesday@@ng-aposphere.cron-editor.wednesday:Wednesday`,
    $localize `:thursday|Thursday@@ng-aposphere.cron-editor.thursday:Thursday`,
    $localize `:friday|Friday@@ng-aposphere.cron-editor.friday:Friday`,
    $localize `:saturday|Saturday@@ng-aposphere.cron-editor.saturday:Saturday`,
    $localize `:sunday|Sunday@@ng-aposphere.cron-editor.sunday:Sunday`,
];
const MONTHS = [
    $localize `:january|January@@ng-aposphere.cron-editor.january:January`,
    $localize `:february|February@@ng-aposphere.cron-editor.february:February`,
    $localize `:march|March@@ng-aposphere.cron-editor.march:March`,
    $localize `:april|April@@ng-aposphere.cron-editor.april:April`,
    $localize `:may|May@@ng-aposphere.cron-editor.may:May`,
    $localize `:june|June@@ng-aposphere.cron-editor.june:June`,
    $localize `:july|July@@ng-aposphere.cron-editor.july:July`,
    $localize `:august|August@@ng-aposphere.cron-editor.august:August`,
    $localize `:september|September@@ng-aposphere.cron-editor.september:September`,
    $localize `:october|October@@ng-aposphere.cron-editor.october:October`,
    $localize `:november|November@@ng-aposphere.cron-editor.november:November`,
    $localize `:december|December@@ng-aposphere.cron-editor.december:December`,
];
const DAY_CRON = [
    'MON',
    'TUE',
    'WED',
    'THU',
    'FRI',
    'SAT',
    'SUN',
];
const MONTH_CRON = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
];
let CronEditorComponent = CronEditorComponent_1 = class CronEditorComponent {
    writeValue(cron) {
        let minuteParsed, hourParsed, dayOfMonthParsed, monthParsed, dayOfWeekParsed;
        const splitMonth = cron.split(' ');
        if (splitMonth.length === 6)
            [, minuteParsed, hourParsed, dayOfMonthParsed, monthParsed, dayOfWeekParsed] = splitMonth;
        else if (splitMonth.length === 5)
            [minuteParsed, hourParsed, dayOfMonthParsed, monthParsed, dayOfWeekParsed] = splitMonth;
        else
            throw new TypeError(`cron must have 5 or 6 elements: '${cron}'`);
        // Months
        const monthFormValue = monthParsed === '*' ?
            Array.from({ length: 12 }).map(() => true) :
            MONTH_CRON.map((m, i) => monthParsed.split(',').includes(m) || monthParsed.split(',').includes(`${i + 1}`));
        // Find Variant
        let typeFormValue;
        if (dayOfMonthParsed === 'L')
            typeFormValue = VARIANTS.LAST_DAY_OF_MONTH;
        else if (dayOfMonthParsed === '?') {
            const splitDay = dayOfWeekParsed.split(',');
            if (splitDay.some(d => d.endsWith('L')))
                typeFormValue = VARIANTS.LAST_SPECIFIC_DAY_OF_MONTH;
            else
                typeFormValue = VARIANTS.DAY_OF_WEEK;
        }
        else
            typeFormValue = VARIANTS.DAY_OF_MONTH;
        let formValue;
        switch (typeFormValue) {
            case VARIANTS.DAY_OF_WEEK: {
                const dayOfWeekFormValue = dayOfWeekParsed === '*' ?
                    Array.from({ length: 7 }).map(() => true) :
                    DAY_CRON.map((day, i) => dayOfWeekParsed.split(',').includes(day) || dayOfWeekParsed.split(',').includes((i + 1).toString()));
                formValue = {
                    type: typeFormValue,
                    months: monthFormValue,
                    time: `${hourParsed.padStart(2, '0')}:${minuteParsed.padStart(2, '0')}`,
                    dayOfWeek: dayOfWeekFormValue,
                };
                break;
            }
            case VARIANTS.DAY_OF_MONTH: {
                const dayOfMonthFormValue = dayOfMonthParsed === '*' ?
                    Array.from({ length: 31 }).map(() => true) :
                    Array.from({ length: 31 }).map((_, i) => dayOfMonthParsed.split(',').includes(`${i + 1}`));
                formValue = {
                    type: typeFormValue,
                    months: monthFormValue,
                    time: `${hourParsed.padStart(2, '0')}:${minuteParsed.padStart(2, '0')}`,
                    dayOfMonth: dayOfMonthFormValue,
                };
                break;
            }
            case VARIANTS.LAST_DAY_OF_MONTH: {
                formValue = {
                    type: typeFormValue,
                    months: monthFormValue,
                    time: `${hourParsed.padStart(2, '0')}:${minuteParsed.padStart(2, '0')}`,
                };
                break;
            }
            case VARIANTS.LAST_SPECIFIC_DAY_OF_MONTH: {
                const daySplit = dayOfWeekParsed.split(',');
                const dayOfWeekFormValue = Array.from({ length: 7 }).map((_, i) => daySplit.includes(`${i + 1}L`));
                formValue = {
                    type: typeFormValue,
                    months: monthFormValue,
                    time: `${hourParsed.padStart(2, '0')}:${minuteParsed.padStart(2, '0')}`,
                    lastSpecificDayOfMonth: dayOfWeekFormValue,
                };
                break;
            }
        }
        this.updateFormValidatorsAndDisabled(formValue);
        this.form.patchValue(formValue, { emitEvent: false });
    }
    registerOnChange(fn) {
        this.parentChangeHandler = fn;
    }
    registerOnTouched(fn) {
        this.parentTouchedHandler = fn;
    }
    setDisabledState(disabled) {
        if (disabled)
            this.form.disable();
        else
            this.form.enable();
    }
    validate() {
        return this.form.valid ?
            null :
            { invalid: true };
    }
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
        /** Bundle for all subscriptions */
        this.subscriptions = new Subscription();
        /** save the handler being registered by consuming component */
        this.parentChangeHandler = () => { };
        /** save the handler being registered by consuming component */
        this.parentTouchedHandler = () => { };
        this.touched = false;
        this.disabled = false;
        /** Form */
        this.form = this.formBuilder.group({
            time: this.formBuilder.control('23:59', Validators.required),
            type: this.formBuilder.control(VARIANTS.DAY_OF_MONTH, [Validators.required]),
            months: this.formBuilder.array(Array.from({ length: 12 }).map((_, i) => [i === 4]), [MinCheckedValidator(1)]),
            dayOfWeek: this.formBuilder.array(Array.from({ length: 7 }).map((_, i) => [i === 4])),
            dayOfMonth: this.formBuilder.array(Array.from({ length: 31 }).map((_, i) => [i === 0])),
            lastSpecificDayOfMonth: this.formBuilder.array(Array.from({ length: 7 }).map((_, i) => [i === 0])),
        });
        /** For use in template */
        this.VARIANTS = VARIANTS;
        /** For use in template */
        this.DAYS = DAYS;
        /** For use in template */
        this.MONTHS = MONTHS;
    }
    ngOnInit() {
        this.subscriptions.add(this.form.valueChanges.subscribe(() => this.onChangeFormValue(this.form.getRawValue())));
        window['cronEditor'] = this;
    }
    /** Handler for new form value. Delegate to the parent handlers */
    triggerChange(v) {
        this.parentChangeHandler(v);
        if (!this.touched) {
            this.parentTouchedHandler();
            this.touched = true;
        }
    }
    /** Handler for updated form value */
    onChangeFormValue(formValue) {
        this.updateFormValidatorsAndDisabled(formValue);
        const seconds = '0';
        const [hours, minutes] = formValue.time.split(':');
        const activeMonths = Object.entries(formValue.months).filter(([_indexString, active]) => active);
        const month = activeMonths.length === 12 ?
            '*' :
            activeMonths.map(([indexString]) => MONTH_CRON[+indexString]).join(',');
        let dayOfMonth, dayOfWeek;
        switch (formValue.type) {
            case VARIANTS.DAY_OF_MONTH: {
                const activeDays = Object.entries(formValue.dayOfMonth).filter(([_indexString, active]) => active);
                dayOfMonth = activeDays.length === 31 ?
                    '*' :
                    activeDays.map(([indexString]) => +indexString + 1).join(',');
                dayOfWeek = '?';
                break;
            }
            case VARIANTS.DAY_OF_WEEK: {
                dayOfMonth = '?';
                const activeDays = Object.entries(formValue.dayOfWeek).filter(([_indexString, active]) => active);
                dayOfWeek = activeDays.length === 7 ?
                    '*' :
                    activeDays.map(([indexString]) => `${DAY_CRON[+indexString]}`).join(',');
                break;
            }
            case VARIANTS.LAST_DAY_OF_MONTH: {
                dayOfMonth = 'L';
                dayOfWeek = '?';
                break;
            }
            case VARIANTS.LAST_SPECIFIC_DAY_OF_MONTH: {
                dayOfMonth = '?';
                dayOfWeek = Object.entries(formValue.lastSpecificDayOfMonth).filter(([_indexString, active]) => active).map(([indexString]) => `${+indexString + 1}L`).join(',');
                break;
            }
        }
        const cron = `${seconds} ${minutes} ${hours} ${dayOfMonth} ${month} ${dayOfWeek}`;
        this.triggerChange(cron);
    }
    /** Set validators for form value */
    updateFormValidatorsAndDisabled(formValue) {
        if (formValue.type === VARIANTS.DAY_OF_MONTH) {
            this.form.controls.dayOfMonth.setValidators([MinCheckedValidator(1)]);
            this.form.controls.dayOfMonth.enable({ emitEvent: false });
        }
        else {
            this.form.controls.dayOfMonth.setValidators([]);
            this.form.controls.dayOfMonth.disable({ emitEvent: false });
        }
        if (formValue.type === VARIANTS.DAY_OF_WEEK) {
            this.form.controls.dayOfWeek.setValidators([MinCheckedValidator(1)]);
            this.form.controls.dayOfWeek.enable({ emitEvent: false });
        }
        else {
            this.form.controls.dayOfWeek.setValidators([]);
            this.form.controls.dayOfWeek.disable({ emitEvent: false });
        }
        if (formValue.type === VARIANTS.LAST_SPECIFIC_DAY_OF_MONTH) {
            this.form.controls.lastSpecificDayOfMonth.setValidators([MinCheckedValidator(1)]);
            this.form.controls.lastSpecificDayOfMonth.enable({ emitEvent: false });
        }
        else {
            this.form.controls.lastSpecificDayOfMonth.setValidators([]);
            this.form.controls.lastSpecificDayOfMonth.disable({ emitEvent: false });
        }
    }
    /** Select or unselect all months */
    setAllMonths(value) {
        this.form.controls.months.patchValue(Array.from({ length: 12 }).map(() => value));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CronEditorComponent, deps: [{ token: i1$1.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CronEditorComponent, selector: "apos-cron-editor", providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                multi: true,
                useExisting: forwardRef(() => CronEditorComponent_1),
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => CronEditorComponent_1),
                multi: true,
            },
        ], ngImport: i0, template: "<form>\n  <!-- months -->\n  <div class=\"mb-3\">\n    <div\n      *ngFor=\"let month of MONTHS; let i = index\"\n      class=\"form-check form-check-inline form-check-inline-month-text\"\n      [class.is-invalid]=\"form.controls.months.invalid\"\n    >\n      <input\n        class=\"form-check-input\"\n        type=\"checkbox\"\n        id=\"monthly-month-{{ i }}-checkbox\"\n        [formControl]=\"form.controls.months.controls[i]\"\n      />\n      <label class=\"form-check-label\" for=\"monthly-month-{{ i }}-checkbox\">{{\n        MONTHS[i]\n      }}</label>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          months-invalid|Invalid month\n          form@@ng-aposphere.cron-editor.months-invalid\"\n        >Please select at least one month</span\n      >\n    </div>\n    <div class=\"d-flex\">\n      <button\n        type=\"button\"\n        class=\"btn btn-link text-decoration-none link-like p-0 me-2\"\n        (click)=\"setAllMonths(true)\"\n      >\n        <span\n          i18n=\"\n            select-all-months|Select all\n            months@@ng-aposphere.cron-editor.select-all-months\"\n          >Select all months</span\n        >\n      </button>\n      <button\n        type=\"button\"\n        class=\"btn btn-link text-decoration-none link-like p-0\"\n        (click)=\"setAllMonths(false)\"\n      >\n        <span\n          i18n=\"\n            unselect-all-months|Unselect all\n            months@@ng-aposphere.cron-editor.unselect-all-months\"\n          >Unselect all months</span\n        >\n      </button>\n    </div>\n  </div>\n\n  <!-- days of month -->\n  <div class=\"form-check\">\n    <input\n      type=\"radio\"\n      id=\"monthlyDaysOfMonthRadio\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.DAY_OF_MONTH\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"monthlyDaysOfMonthRadio\"\n      class=\"form-check-label\"\n      i18n=\"day-of-month|Day(s) of Month@@ng-aposphere.cron-editor.day-of-month\"\n      >Day(s) of month:</label\n    >\n    <div [class.is-invalid]=\"form.controls.dayOfMonth.invalid\">\n      <div\n        *ngFor=\"let _ of [].constructor(31); let i = index\"\n        class=\"form-check form-check-inline form-check-inline-day-index\"\n      >\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"monthly-dayofmonth-{{ i }}-checkbox\"\n          [formControl]=\"$any(form.controls.dayOfMonth).controls[i]\"\n        />\n        <label\n          class=\"form-check-label\"\n          for=\"monthly-dayofmonth-{{ i }}-checkbox\"\n          >{{ i + 1 }}</label\n        >\n      </div>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          days-invalid|Invalid days\n          form@@ng-aposphere.cron-editor.day-of-month.days-invalid\"\n        >Please select at least one day</span\n      >\n    </div>\n  </div>\n\n  <!-- days of week -->\n  <div class=\"form-check\">\n    <input\n      type=\"radio\"\n      id=\"monthlyDaysOfWeekRadio\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.DAY_OF_WEEK\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"monthlyDaysOfWeekRadio\"\n      class=\"form-check-label\"\n      i18n=\"day-of-week|Day(s) of Week@@ng-aposphere.cron-editor.day-of-week\"\n      >Day(s) of week:</label\n    >\n    <div [class.is-invalid]=\"form.controls.dayOfWeek.invalid\">\n      <div\n        *ngFor=\"let day of DAYS; let i = index\"\n        class=\"form-check form-check-inline form-check-inline-day-text\"\n      >\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"monthly-dayofweek-{{ i }}-checkbox\"\n          [formControl]=\"$any(form.controls.dayOfWeek).controls[i]\"\n        />\n        <label\n          class=\"form-check-label\"\n          for=\"monthly-dayofweek-{{ i }}-checkbox\"\n          >{{ DAYS[i] }}</label\n        >\n      </div>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          days-invalid|Invalid days\n          form@@ng-aposphere.cron-editor.day-of-week.days-invalid\"\n        >Please select at least one day</span\n      >\n    </div>\n  </div>\n\n  <!-- last day of month -->\n  <div class=\"form-check\">\n    <input\n      type=\"radio\"\n      id=\"monthlyLastDay\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.LAST_DAY_OF_MONTH\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"monthlyLastDay\"\n      class=\"form-check-label\"\n      i18n=\"\n        last-day-of-month|Last Day of\n        Month@@ng-aposphere.cron-editor.last-day-of-month\"\n      >Last day of month</label\n    >\n  </div>\n\n  <!-- last specific day of month-->\n  <div class=\"form-check mb-4\">\n    <input\n      type=\"radio\"\n      id=\"lastSpecificDayOfMonth\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.LAST_SPECIFIC_DAY_OF_MONTH\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"lastSpecificDayOfMonth\"\n      class=\"form-check-label\"\n      i18n=\"\n        last-specific-day-of-month|Last specific Day(s) of\n        Month@@ng-aposphere.cron-editor.last-specific-day-of-month\"\n      >Last specific day(s) of month:</label\n    >\n    <div [class.is-invalid]=\"form.controls.lastSpecificDayOfMonth.invalid\">\n      <div\n        *ngFor=\"let day of DAYS; let i = index\"\n        class=\"form-check form-check-inline form-check-inline-day-text\"\n      >\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"monthly-lastspecificday-{{ i }}-checkbox\"\n          [formControl]=\"$any(form.controls.lastSpecificDayOfMonth).controls[i]\"\n        />\n        <label\n          class=\"form-check-label\"\n          for=\"monthly-lastspecificday-{{ i }}-checkbox\"\n          >{{ DAYS[i] }}</label\n        >\n      </div>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          days-invalid|Invalid days\n          form@@ng-aposphere.cron-editor.last-specific-day-of-month.days-invalid\"\n        >Please select at least one day</span\n      >\n    </div>\n  </div>\n\n  <!-- Time -->\n  <div class=\"d-flex align-items-baseline flex-wrap\">\n    <span i18n=\"at-time|At time@@ng-aposphere.cron-editor.at-time\">\n      At\n      <input\n        type=\"time\"\n        [formControl]=\"form.controls.time\"\n        step=\"60\"\n        required\n      />\n    </span>\n  </div>\n</form>\n", styles: [".inline-select{width:unset}.inline-input{min-width:auto;max-width:none;flex-basis:6rem}.form-check-inline-day-index{min-width:3rem}.form-check-inline-day-text{min-width:6rem}.form-check-inline-month-text{min-width:7rem}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i1$1.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1$1.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }] }); }
};
CronEditorComponent = CronEditorComponent_1 = __decorate([
    AutoUnsubscribe()
], CronEditorComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CronEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-cron-editor', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => CronEditorComponent),
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => CronEditorComponent),
                            multi: true,
                        },
                    ], template: "<form>\n  <!-- months -->\n  <div class=\"mb-3\">\n    <div\n      *ngFor=\"let month of MONTHS; let i = index\"\n      class=\"form-check form-check-inline form-check-inline-month-text\"\n      [class.is-invalid]=\"form.controls.months.invalid\"\n    >\n      <input\n        class=\"form-check-input\"\n        type=\"checkbox\"\n        id=\"monthly-month-{{ i }}-checkbox\"\n        [formControl]=\"form.controls.months.controls[i]\"\n      />\n      <label class=\"form-check-label\" for=\"monthly-month-{{ i }}-checkbox\">{{\n        MONTHS[i]\n      }}</label>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          months-invalid|Invalid month\n          form@@ng-aposphere.cron-editor.months-invalid\"\n        >Please select at least one month</span\n      >\n    </div>\n    <div class=\"d-flex\">\n      <button\n        type=\"button\"\n        class=\"btn btn-link text-decoration-none link-like p-0 me-2\"\n        (click)=\"setAllMonths(true)\"\n      >\n        <span\n          i18n=\"\n            select-all-months|Select all\n            months@@ng-aposphere.cron-editor.select-all-months\"\n          >Select all months</span\n        >\n      </button>\n      <button\n        type=\"button\"\n        class=\"btn btn-link text-decoration-none link-like p-0\"\n        (click)=\"setAllMonths(false)\"\n      >\n        <span\n          i18n=\"\n            unselect-all-months|Unselect all\n            months@@ng-aposphere.cron-editor.unselect-all-months\"\n          >Unselect all months</span\n        >\n      </button>\n    </div>\n  </div>\n\n  <!-- days of month -->\n  <div class=\"form-check\">\n    <input\n      type=\"radio\"\n      id=\"monthlyDaysOfMonthRadio\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.DAY_OF_MONTH\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"monthlyDaysOfMonthRadio\"\n      class=\"form-check-label\"\n      i18n=\"day-of-month|Day(s) of Month@@ng-aposphere.cron-editor.day-of-month\"\n      >Day(s) of month:</label\n    >\n    <div [class.is-invalid]=\"form.controls.dayOfMonth.invalid\">\n      <div\n        *ngFor=\"let _ of [].constructor(31); let i = index\"\n        class=\"form-check form-check-inline form-check-inline-day-index\"\n      >\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"monthly-dayofmonth-{{ i }}-checkbox\"\n          [formControl]=\"$any(form.controls.dayOfMonth).controls[i]\"\n        />\n        <label\n          class=\"form-check-label\"\n          for=\"monthly-dayofmonth-{{ i }}-checkbox\"\n          >{{ i + 1 }}</label\n        >\n      </div>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          days-invalid|Invalid days\n          form@@ng-aposphere.cron-editor.day-of-month.days-invalid\"\n        >Please select at least one day</span\n      >\n    </div>\n  </div>\n\n  <!-- days of week -->\n  <div class=\"form-check\">\n    <input\n      type=\"radio\"\n      id=\"monthlyDaysOfWeekRadio\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.DAY_OF_WEEK\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"monthlyDaysOfWeekRadio\"\n      class=\"form-check-label\"\n      i18n=\"day-of-week|Day(s) of Week@@ng-aposphere.cron-editor.day-of-week\"\n      >Day(s) of week:</label\n    >\n    <div [class.is-invalid]=\"form.controls.dayOfWeek.invalid\">\n      <div\n        *ngFor=\"let day of DAYS; let i = index\"\n        class=\"form-check form-check-inline form-check-inline-day-text\"\n      >\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"monthly-dayofweek-{{ i }}-checkbox\"\n          [formControl]=\"$any(form.controls.dayOfWeek).controls[i]\"\n        />\n        <label\n          class=\"form-check-label\"\n          for=\"monthly-dayofweek-{{ i }}-checkbox\"\n          >{{ DAYS[i] }}</label\n        >\n      </div>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          days-invalid|Invalid days\n          form@@ng-aposphere.cron-editor.day-of-week.days-invalid\"\n        >Please select at least one day</span\n      >\n    </div>\n  </div>\n\n  <!-- last day of month -->\n  <div class=\"form-check\">\n    <input\n      type=\"radio\"\n      id=\"monthlyLastDay\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.LAST_DAY_OF_MONTH\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"monthlyLastDay\"\n      class=\"form-check-label\"\n      i18n=\"\n        last-day-of-month|Last Day of\n        Month@@ng-aposphere.cron-editor.last-day-of-month\"\n      >Last day of month</label\n    >\n  </div>\n\n  <!-- last specific day of month-->\n  <div class=\"form-check mb-4\">\n    <input\n      type=\"radio\"\n      id=\"lastSpecificDayOfMonth\"\n      [formControl]=\"form.controls.type\"\n      name=\"monthlyType\"\n      [value]=\"VARIANTS.LAST_SPECIFIC_DAY_OF_MONTH\"\n      class=\"form-check-input flex-shrink-0\"\n      autocomplete=\"off\"\n    />\n    <label\n      for=\"lastSpecificDayOfMonth\"\n      class=\"form-check-label\"\n      i18n=\"\n        last-specific-day-of-month|Last specific Day(s) of\n        Month@@ng-aposphere.cron-editor.last-specific-day-of-month\"\n      >Last specific day(s) of month:</label\n    >\n    <div [class.is-invalid]=\"form.controls.lastSpecificDayOfMonth.invalid\">\n      <div\n        *ngFor=\"let day of DAYS; let i = index\"\n        class=\"form-check form-check-inline form-check-inline-day-text\"\n      >\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"monthly-lastspecificday-{{ i }}-checkbox\"\n          [formControl]=\"$any(form.controls.lastSpecificDayOfMonth).controls[i]\"\n        />\n        <label\n          class=\"form-check-label\"\n          for=\"monthly-lastspecificday-{{ i }}-checkbox\"\n          >{{ DAYS[i] }}</label\n        >\n      </div>\n    </div>\n    <div class=\"invalid-feedback mb-1\">\n      <span\n        i18n=\"\n          days-invalid|Invalid days\n          form@@ng-aposphere.cron-editor.last-specific-day-of-month.days-invalid\"\n        >Please select at least one day</span\n      >\n    </div>\n  </div>\n\n  <!-- Time -->\n  <div class=\"d-flex align-items-baseline flex-wrap\">\n    <span i18n=\"at-time|At time@@ng-aposphere.cron-editor.at-time\">\n      At\n      <input\n        type=\"time\"\n        [formControl]=\"form.controls.time\"\n        step=\"60\"\n        required\n      />\n    </span>\n  </div>\n</form>\n", styles: [".inline-select{width:unset}.inline-input{min-width:auto;max-width:none;flex-basis:6rem}.form-check-inline-day-index{min-width:3rem}.form-check-inline-day-text{min-width:6rem}.form-check-inline-month-text{min-width:7rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.FormBuilder }]; } });

class AposFormsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposFormsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposFormsModule, declarations: [CronEditorComponent], imports: [CommonModule,
            ReactiveFormsModule], exports: [CronEditorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposFormsModule, imports: [CommonModule,
            ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposFormsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CronEditorComponent,
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                    ],
                    exports: [
                        CronEditorComponent,
                    ],
                }]
        }] });

/** Default color (RGB dark blue) */
const DEFAULT_COLOR = 'rgb(0, 123, 255)';
/** Tooltip colors */
const TOOLTIP_COLOR = {
    BACKGROUND: '#f8f9fa',
    BORDER: '#ced4da',
    TEXT: '#000',
};

/** The default number of significant digits */
const DEFAULT_SIGNIFICANT_DIGITS = 2;
/** The max tick length */
const MAX_TICK_LABEL_LENGTH = 15;
/** Date format: https://momentjs.com/docs/#/displaying/format/ */
const DATE_FORMAT_READABLE = 'll';
/** Timestamp format: https://momentjs.com/docs/#/displaying/format/ */
const TIMESTAMP_FORMAT_READABLE = 'lll';
/** Tooltip styles */
const TOOLTIP_STYLES = {
    backgroundColor: TOOLTIP_COLOR.BACKGROUND,
    borderColor: TOOLTIP_COLOR.BORDER,
    titleColor: TOOLTIP_COLOR.TEXT,
    bodyColor: TOOLTIP_COLOR.TEXT,
    borderWidth: 1,
    cornerRadius: 1,
};

class BarChartComponent {
    constructor() {
        /** Data (input subject) */
        this._inputData$ = new ReplaySubject(1);
        /** Axes (input subject) */
        this._inputAxes$ = new BehaviorSubject({});
        /** Elements visibility (input subject) */
        this._inputElementsVisibility$ = new BehaviorSubject({});
        /** The Chart data for chartjs */
        this._data$ = this._inputData$.pipe(filter(inputDataPoints => Boolean(inputDataPoints)), map$1(inputDataPoints => inputDataPoints.map(d => ({
            ...d,
            color: colorLib(d.color || DEFAULT_COLOR),
        }))), map$1(inputDataPoints => ({
            labels: inputDataPoints.map(d => d.key),
            datasets: [{
                    data: inputDataPoints.map(d => d.value),
                    backgroundColor: (context) => inputDataPoints[context.dataIndex].color.clone().alpha(0.2).rgbString(),
                    borderColor: (context) => inputDataPoints[context.dataIndex].color.clone().alpha(1).rgbString(),
                    hoverBackgroundColor: (context) => inputDataPoints[context.dataIndex].color.clone().alpha(0.5).rgbString(),
                    hoverBorderColor: (context) => inputDataPoints[context.dataIndex].color.clone().alpha(1).rgbString(),
                    borderWidth: 1,
                    borderRadius: 3,
                }],
        })));
        /** Whether to show legend */
        this._showLegend$ = new BehaviorSubject(false);
        /** The Chart options */
        this._options$ = combineLatest([
            this._inputAxes$,
            this._inputElementsVisibility$,
        ]).pipe(map$1(([inputAxes, inputElementsVisibility]) => ({
            maintainAspectRatio: false,
            responsive: true,
            resizeDelay: 10,
            scales: {
                x: {
                    title: {
                        display: !!inputAxes.x,
                        text: inputAxes.x,
                    },
                    display: inputElementsVisibility.xAxis !== false,
                    grid: {
                        display: inputElementsVisibility.xGrid !== false,
                    },
                },
                y: {
                    title: {
                        display: !!inputAxes.y,
                        text: inputAxes.y,
                    },
                    display: inputElementsVisibility.yAxis !== false,
                    grid: {
                        display: inputElementsVisibility.yGrid !== false,
                    },
                },
            },
            plugins: {
                tooltip: { ...TOOLTIP_STYLES },
            },
        })));
    }
    /**
     * Data (input setter)
     * The data (one entry per bar): [{ key, value }, ...]
     */
    set data(data) { this._inputData$.next(data); }
    /**
     * Axes (input setter)
     * The axes labels (not displayed if left undefined)
     */
    set axes(axes) { this._inputAxes$.next(axes); }
    /**
     * Elements visiblity (input setter)
     * The visibility of the charts elements
     */
    set elementsVisibility(elementsVisibility) { this._inputElementsVisibility$.next(elementsVisibility); }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BarChartComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BarChartComponent, selector: "apos-bar-chart", inputs: { data: "data", axes: "axes", elementsVisibility: "elementsVisibility" }, ngImport: i0, template: "<div class=\"position-relative w-100 h-100\">\n  <canvas\n    *ngIf=\"\n      (_data$ | async) !== null &&\n      (_options$ | async) !== null &&\n      (_showLegend$ | async) !== null\n    \"\n    baseChart\n    [type]=\"'bar'\"\n    [data]=\"_data$ | async\"\n    [options]=\"_options$ | async\"\n    [legend]=\"_showLegend$ | async\"\n    aria-label=\"Bar Chart\"\n    role=\"img\"\n  ></canvas>\n</div>\n", styles: [":host{display:block;height:100%;width:100%}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.BaseChartDirective, selector: "canvas[baseChart]", inputs: ["type", "legend", "data", "options", "plugins", "labels", "datasets"], outputs: ["chartClick", "chartHover"], exportAs: ["base-chart"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BarChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-bar-chart', template: "<div class=\"position-relative w-100 h-100\">\n  <canvas\n    *ngIf=\"\n      (_data$ | async) !== null &&\n      (_options$ | async) !== null &&\n      (_showLegend$ | async) !== null\n    \"\n    baseChart\n    [type]=\"'bar'\"\n    [data]=\"_data$ | async\"\n    [options]=\"_options$ | async\"\n    [legend]=\"_showLegend$ | async\"\n    aria-label=\"Bar Chart\"\n    role=\"img\"\n  ></canvas>\n</div>\n", styles: [":host{display:block;height:100%;width:100%}\n"] }]
        }], propDecorators: { data: [{
                type: Input
            }], axes: [{
                type: Input
            }], elementsVisibility: [{
                type: Input
            }] } });

class AposBarChartsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposBarChartsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposBarChartsModule, declarations: [BarChartComponent], imports: [CommonModule,
            NgChartsModule], exports: [BarChartComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposBarChartsModule, imports: [CommonModule,
            NgChartsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposBarChartsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BarChartComponent,
                    ],
                    imports: [
                        CommonModule,
                        NgChartsModule,
                    ],
                    exports: [
                        BarChartComponent,
                    ],
                }]
        }] });

class ColorsService {
    /**
     * Rotate HSL colors
     * @param index The integer rotation index (different index yields different color)
     * @param saturation The saturation between 0 and 1
     * @param lightness The lightness between 0 and 1
     */
    rotateColorCustom(index, saturation, lightness) {
        const hsl = `hsl(${(index || 0) * 137.508},${(saturation * 100).toFixed()}%,${(lightness * 100).toFixed()}%)`;
        return hsl;
    }
    /**
     * Rotate HSL colors for background (light colors)
     * @param index The integer rotation index (different index yields different color)
     */
    rotateColorBackground(index) {
        return this.rotateColorCustom(index, 0.5, 0.9);
    }
    /**
     * Rotate HSL colors for solid/border (dark colors)
     * @param index The integer rotation index (different index yields different color)
     */
    rotateColorSolid(index) {
        return this.rotateColorCustom(index, 0.5, 0.5);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ColorsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ColorsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ColorsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const BORDER_DASH_DATA = {
    'solid': [],
    'dashed': [6, 2],
    'dotted': [2, 2],
};
const BORDER_DASH_MARKER = {
    'solid': [],
    'dashed': [6, 3],
    'dotted': [1, 1],
};
const BORDER_DASH_LIMITS = [5, 3];
/** Light alpha */
const LIGHT_ALPHA = 0.1;
/** Total y-extent will be (max-min)*(1+PADDING_FACTOR) */
const PADDING_FACTOR = {
    X: 0.03,
    Y: 0.12,
};
/** The minimal size of the chart area in value space */
const MIN_SIZE = {
    X: {
        TIME: moment.duration(1, 'day'),
        NUMBER: 1,
    },
    Y: 1,
};
/** Localized Terms */
const TERMS = {
    DATA: $localize `:data|Data in graph@@ng-aposphere.line-chart.data:Average`,
    UPPER_LIMIT: $localize `:upper-limit|Upper limit in graph@@ng-aposphere.line-chart.upper-limit:Upper uncertainty`,
    LOWER_LIMIT: $localize `:lower-limit|Lower limit in graph@@ng-aposphere.line-chart.lower-limit:Lower uncertainty`,
};
// @dynamic
class LineChartComponent {
    /**
     * Data (input setter)
     * The data (one entry per line): [{ key, value }, ...]
     */
    set data(data) { this._inputData$.next(data); }
    /**
     * Axes (input setter)
     * The axes labels (not displayed if left undefined)
     */
    set axes(axes) { this._inputAxes$.next(axes); }
    /**
     * Elements visiblity (input setter)
     * The visibility of the charts elements
     */
    set elementsVisibility(elementsVisibility) { this._inputElementsVisibility$.next(elementsVisibility); }
    /** Additional marker lines (input setter) */
    set markerLines(markerLines) { this._inputMarkerLines$.next(markerLines); }
    /**
     * tooltip rendering functions
     * Chart.js docs: https://www.chartjs.org/docs/latest/configuration/tooltip.html#tooltip-callbacks
     */
    static { this.tooltipFunctions = (ticks, datasetIndicesMap, terms, xUnit, yUnit) => ({
        title: (items) => {
            const xValues = items
                .map(item => {
                const dataPoint = item.raw;
                if (typeof dataPoint.x === 'string')
                    return moment(dataPoint.x).format(DATE_FORMAT_READABLE);
                else {
                    let label = '';
                    label = `${label}${Number(dataPoint.x.toPrecision(DEFAULT_SIGNIFICANT_DIGITS)).toString()}`;
                    if (xUnit)
                        label = `${label} ${xUnit}`;
                    return label;
                }
            })
                // filter unique
                .filter((value, index, self) => self.indexOf(value) === index);
            return xValues.join('/ ');
        },
        label: (item) => {
            const datasetLabel = item.dataset.label;
            const dataPoint = item.raw;
            let valueString = Number(dataPoint.y.toPrecision(DEFAULT_SIGNIFICANT_DIGITS)).toString();
            // Unit
            if (yUnit)
                valueString = `${valueString} ${yUnit}`;
            // Dataset type: Estimate or upper/lower limit
            let datasetTypeLabel;
            if (datasetIndicesMap.get(item.datasetIndex) && Object.values(datasetIndicesMap.get(item.datasetIndex)).length > 0)
                datasetTypeLabel = terms.DATA;
            else if (Array.from(datasetIndicesMap).some(([_parentIndex, limitIndices]) => item.datasetIndex === limitIndices.upperLimit))
                datasetTypeLabel = terms.UPPER_LIMIT;
            else if (Array.from(datasetIndicesMap).some(([_parentIndex, limitIndices]) => item.datasetIndex === limitIndices.lowerLimit))
                datasetTypeLabel = terms.LOWER_LIMIT;
            // Use dataset label
            let label = '';
            if (datasetTypeLabel && datasetLabel)
                label = `${datasetLabel} [${datasetTypeLabel}]: ${label}`;
            else if (datasetLabel)
                label = `${datasetLabel}: ${label}`;
            else if (datasetTypeLabel)
                label = `[${datasetTypeLabel}]: ${label}`;
            // Map to closest tick and display the tick label if applicable
            const closestTick = ticks ?
                LineChartComponent.closestScaleTick(dataPoint.y, ticks) :
                undefined;
            if (closestTick && closestTick.label)
                label = `${label}${LineChartComponent.stringEllipsis(closestTick.label, MAX_TICK_LABEL_LENGTH)} (${valueString})`;
            else
                label = `${label}${valueString}`;
            return label;
        },
    }); }
    /**
     * Tick rendering function for x axis
     * Chart.js docs: https://www.chartjs.org/docs/latest/samples/scale-options/ticks.html
     */
    static { this.renderXTicks = (unit) => {
        return function (val) {
            let label = '';
            // round tick value
            const valueString = typeof val === 'number' ?
                Number(val.toPrecision(DEFAULT_SIGNIFICANT_DIGITS)).toString() :
                val;
            // Value
            label = `${label}${valueString}`;
            // Unit
            if (unit)
                label = `${label} ${unit}`;
            return label;
        };
    }; }
    /**
     * Tick rendering function for y axis
     * Chart.js docs: https://www.chartjs.org/docs/latest/samples/scale-options/ticks.html
     */
    static { this.renderYTicks = (ticks, unit) => {
        return function (val) {
            let label = '';
            // Map to closest tick and display the tick label if applicable
            if (ticks) {
                const closestTick = LineChartComponent.closestScaleTick(val, ticks);
                if (closestTick.label)
                    label = `${label}${LineChartComponent.stringEllipsis(closestTick.label, MAX_TICK_LABEL_LENGTH)} \u2012 `;
            }
            // Round tick value if no specified ticks
            const valueString = ticks ?
                val :
                Number(val.toPrecision(DEFAULT_SIGNIFICANT_DIGITS)).toString();
            // Value
            label = `${label}${valueString}`;
            // Unit
            if (unit)
                label = `${label} ${unit}`;
            return label;
        };
    }; }
    /** Get closest tick */
    static closestScaleTick(queryValue, ticks) {
        let closest = ticks[0];
        ticks.forEach(tick => {
            if (Math.abs(queryValue - tick.value) < Math.abs(queryValue - closest.value)) {
                closest = tick;
            }
        });
        return closest;
    }
    /** Add ellipsis if string is too long */
    static stringEllipsis(str, maxLength) {
        if (str.length < maxLength - 1)
            return str;
        else
            return `${str.slice(0, maxLength - 1)}…`;
    }
    /**
     * Get the gradient for the ticks
     * Chart.js docs: https://www.chartjs.org/docs/latest/samples/advanced/linear-gradient.html
     */
    getTicksGradients(ctx, chartArea, ticks, dataBounds) {
        /* istanbul ignore if */
        if (!chartArea)
            return { opaque: undefined, semitransparent: undefined }; // this happens on initial chart load
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        /* istanbul ignore next */
        const changed = !this._gradientCache.opaqueGradient || !this._gradientCache.semitransparentGradient || this._gradientCache.width !== chartWidth || this._gradientCache.height !== chartHeight;
        if (changed) {
            // Create the gradient because this is either the first render
            // or the size of the chart has changed
            this._gradientCache.width = chartWidth;
            this._gradientCache.height = chartHeight;
            this._gradientCache.opaqueGradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            this._gradientCache.semitransparentGradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            const sortedTicks = Array.from(ticks);
            sortedTicks.sort((a, b) => a.value - b.value);
            for (const tick of sortedTicks) {
                const normalizedValue = (tick.value - dataBounds.minY) / (dataBounds.maxY - dataBounds.minY);
                /* istanbul ignore else */
                this._gradientCache.opaqueGradient.addColorStop(normalizedValue, colorLib(tick.color || 'black').alpha(1).rgbString());
                this._gradientCache.semitransparentGradient.addColorStop(normalizedValue, colorLib(tick.color || 'black').alpha(0.2).rgbString());
            }
            this._gradientCache.opaqueGradient.addColorStop(1, colorLib('white').alpha(1).rgbString()); // to avoid color showing up in legend
        }
        return {
            opaque: this._gradientCache.opaqueGradient,
            semitransparent: this._gradientCache.semitransparentGradient,
        };
    }
    constructor(colorsService) {
        this.colorsService = colorsService;
        /** Data (input subject) */
        this._inputData$ = new ReplaySubject(1);
        /** Axes (input subject) */
        this._inputAxes$ = new ReplaySubject();
        /** Elements visibility (input subject) */
        this._inputElementsVisibility$ = new BehaviorSubject({});
        /** Additional marker lines (input subject) */
        this._inputMarkerLines$ = new BehaviorSubject({});
        /** Group main dataline and limit lines. Indexed by main dataline datasetIndex. Markers are stored as empty object */
        this.parentDatasetsMap = new Map();
        /** Terms generated from the input dataset */
        this._terms$ = this._inputData$.pipe(map$1((datasets) => ({
            DATA: datasets[0].termData || TERMS.DATA,
            UPPER_LIMIT: datasets[0].termUpperLimit || TERMS.UPPER_LIMIT,
            LOWER_LIMIT: datasets[0].termLowerLimit || TERMS.LOWER_LIMIT,
        })));
        /** Bounds of the chart */
        this._bounds$ = combineLatest([
            this._inputData$,
            this._inputAxes$,
            this._inputMarkerLines$,
        ]).pipe(filter(([inputDatasets, inputAxes, inputMarkerLines]) => Boolean(inputDatasets && inputAxes && inputMarkerLines)), map$1(([inputDatasets, inputAxes, inputMarkerLines]) => {
            const bounds = {
                minX: inputAxes.x.type === 'number' ? Infinity : moment().add(1000, 'years').format(),
                maxX: inputAxes.x.type === 'number' ? -Infinity : moment().subtract(1000, 'years').format(),
                minY: Infinity,
                maxY: -Infinity,
            };
            // consider input datasets
            inputDatasets.forEach(dataset => {
                dataset.dataPoints.forEach(dataPoint => {
                    if (inputAxes.x.type === 'number') {
                        if (dataPoint.x < bounds.minX)
                            bounds.minX = dataPoint.x;
                        if (dataPoint.x > bounds.maxX)
                            bounds.maxX = dataPoint.x;
                    }
                    else {
                        if (moment(dataPoint.x).isBefore(bounds.minX))
                            bounds.minX = moment(dataPoint.x).format();
                        if (moment(dataPoint.x).isAfter(bounds.maxX))
                            bounds.maxX = moment(dataPoint.x).format();
                    }
                    if ((dataPoint.lowerLimit ?? dataPoint.y) < bounds.minY)
                        bounds.minY = dataPoint.lowerLimit ?? dataPoint.y;
                    if ((dataPoint.upperLimit ?? dataPoint.y) > bounds.maxY)
                        bounds.maxY = dataPoint.upperLimit ?? dataPoint.y;
                });
            });
            // consider marker lines
            inputMarkerLines.horizontal?.forEach(l => {
                if (l.coordinate < bounds.minY)
                    bounds.minY = l.coordinate;
                if (l.coordinate > bounds.maxY)
                    bounds.maxY = l.coordinate;
            });
            inputMarkerLines.vertical?.forEach(l => {
                if (inputAxes.x.type === 'number') {
                    if (l.coordinate < bounds.minX)
                        bounds.minX = l.coordinate;
                    if (l.coordinate > bounds.maxX)
                        bounds.maxX = l.coordinate;
                }
                else {
                    if (moment(l.coordinate).isBefore(bounds.minX))
                        bounds.minX = moment(l.coordinate).format();
                    if (moment(l.coordinate).isAfter(bounds.maxX))
                        bounds.maxX = moment(l.coordinate).format();
                }
            });
            // consider ticks
            if (inputAxes.y.ticks) {
                const tickValues = inputAxes.y.ticks.map(t => t.value);
                const minTickValue = Math.min(...tickValues);
                const maxTickValue = Math.max(...tickValues);
                if (minTickValue < bounds.minY)
                    bounds.minY = minTickValue;
                if (maxTickValue > bounds.maxY)
                    bounds.maxY = maxTickValue;
            }
            // add x-padding and x-minsize
            if (inputAxes.x.type === 'number') {
                let xSpan = bounds.maxX - bounds.minX;
                if (xSpan < MIN_SIZE.X.NUMBER) {
                    const deltaX = MIN_SIZE.X.NUMBER - xSpan;
                    bounds.minX = bounds.minX - deltaX / 2;
                    bounds.maxX = bounds.maxX + deltaX / 2;
                    xSpan = MIN_SIZE.X.NUMBER;
                }
                bounds.minX = bounds.minX - xSpan * (PADDING_FACTOR.X / 2);
                bounds.maxX = bounds.maxX + xSpan * (PADDING_FACTOR.X / 2);
            }
            else {
                let xSpan = moment(bounds.maxX).diff(bounds.minX);
                if (xSpan < MIN_SIZE.X.TIME.asMilliseconds()) {
                    const deltaX = MIN_SIZE.X.TIME.asMilliseconds() - xSpan;
                    bounds.minX = moment(bounds.minX).subtract(deltaX / 2).format();
                    bounds.maxX = moment(bounds.maxX).add(deltaX / 2).format();
                    xSpan = MIN_SIZE.X.TIME.asMilliseconds();
                }
                bounds.minX = moment(bounds.minX).subtract(xSpan * (PADDING_FACTOR.X / 2), 'ms').format();
                bounds.maxX = moment(bounds.maxX).add(xSpan * (PADDING_FACTOR.X / 2), 'ms').format();
            }
            // y-minsize
            let ySpan = bounds.maxY - bounds.minY;
            if (ySpan < MIN_SIZE.Y) {
                const deltaY = MIN_SIZE.Y - ySpan;
                bounds.minY -= deltaY / 2;
                bounds.maxY += deltaY / 2;
                ySpan = MIN_SIZE.Y;
            }
            // add y-padding
            bounds.minY -= ySpan * (PADDING_FACTOR.Y / 2);
            bounds.maxY += ySpan * (PADDING_FACTOR.Y / 2);
            return bounds;
        }), shareReplay(1));
        /** The Chart data for chartjs */
        this._data$ = combineLatest([
            this._inputData$,
            this._inputAxes$,
            this._inputMarkerLines$,
            this._bounds$,
        ]).pipe(filter(([inputDatasets, inputAxes, inputMarkerLines, _bounds]) => Boolean(inputDatasets && inputAxes && inputMarkerLines)), map$1(([inputDatasets, inputAxes, inputMarkerLines, bounds]) => {
            let colorIndex = 0;
            return {
                inputDatasets: inputDatasets.map((d) => ({
                    ...d,
                    dataPoints: d.dataPoints
                        .filter(p => (inputAxes.x.type === 'number' && Number.isFinite(p.x) ||
                        inputAxes.x.type === 'time' && moment(p.x).isValid()) &&
                        Number.isFinite(p.y))
                        .map(p => ({
                        ...p,
                        x: inputAxes.x.type === 'number' ?
                            p.x :
                            moment(p.x).format(),
                    })),
                    color: colorLib(d.color || this.colorsService.rotateColorSolid(colorIndex++)),
                })),
                inputMarkerLines: {
                    horizontal: inputMarkerLines.horizontal?.map(m => ({
                        ...m,
                        color: colorLib(m.color || this.colorsService.rotateColorSolid(colorIndex++)),
                    })) ?? [],
                    vertical: inputMarkerLines.vertical?.map(m => ({
                        ...m,
                        coordinate: inputAxes.x.type === 'number' ?
                            m.coordinate :
                            moment(m.coordinate).format(),
                        color: colorLib(m.color || this.colorsService.rotateColorSolid(colorIndex++)),
                    })) ?? [],
                },
                bounds,
                inputAxes,
            };
        }), map$1(({ inputDatasets, inputMarkerLines, bounds, inputAxes }) => {
            const chartData = { datasets: [] };
            // Gradient is shown for a single dataset only. For multiple datasets, colors are needed for visual distinction
            const showGradient = inputDatasets.length === 1 && inputAxes.y.ticks;
            // datasets: each map to upper limit, data, lower limit
            inputDatasets.forEach(inputDataset => {
                const opaqueColor = showGradient ?
                    ({ chart: { ctx, chartArea } }) => this.getTicksGradients(ctx, chartArea, inputAxes.y.ticks, bounds).opaque :
                    inputDataset.color.clone().alpha(1).rgbString();
                const semitransparentColor = showGradient ?
                    ({ chart: { ctx, chartArea } }) => this.getTicksGradients(ctx, chartArea, inputAxes.y.ticks, bounds).semitransparent :
                    inputDataset.color.clone().alpha(LIGHT_ALPHA).rgbString();
                const showLimits = inputDataset.dataPoints.some(d => Number.isFinite(d.lowerLimit) || Number.isFinite(d.upperLimit));
                // Upper limit
                if (showLimits) {
                    chartData.datasets.push({
                        label: inputDataset.label || '',
                        data: inputDataset.dataPoints.map(d => ({ x: d.x, y: d.upperLimit ?? d.y })),
                        showLine: true,
                        borderWidth: 1,
                        tension: 0.2,
                        borderColor: opaqueColor,
                        pointBackgroundColor: opaqueColor,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        pointHitRadius: 10,
                        borderDash: BORDER_DASH_LIMITS,
                    });
                }
                // Data
                chartData.datasets.push({
                    label: inputDataset.label || '',
                    data: inputDataset.dataPoints.map(d => ({ x: d.x, y: d.y })),
                    showLine: true,
                    borderWidth: 2,
                    tension: 0.1,
                    borderColor: opaqueColor,
                    pointBorderColor: opaqueColor,
                    pointBackgroundColor: opaqueColor,
                    pointHoverBorderColor: opaqueColor,
                    pointHoverBackgroundColor: semitransparentColor,
                    pointBorderWidth: 0,
                    pointHoverBorderWidth: 2,
                    pointRadius: 4,
                    pointHitRadius: 10,
                    borderDash: BORDER_DASH_DATA[inputDataset.lineStyle || 'solid'],
                    hidden: false,
                });
                // Lower limit
                if (showLimits) {
                    chartData.datasets.push({
                        label: inputDataset.label || '',
                        data: inputDataset.dataPoints.map(d => ({ x: d.x, y: d.lowerLimit ?? d.y })),
                        showLine: true,
                        borderWidth: 1,
                        tension: 0.2,
                        borderColor: opaqueColor,
                        pointBackgroundColor: opaqueColor,
                        backgroundColor: semitransparentColor,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        pointHitRadius: 10,
                        borderDash: BORDER_DASH_LIMITS,
                        fill: chartData.datasets.length - 2,
                        hidden: false,
                    });
                }
                if (showLimits)
                    this.parentDatasetsMap.set(chartData.datasets.length - 2, { upperLimit: chartData.datasets.length - 3, lowerLimit: chartData.datasets.length - 1 });
                else
                    this.parentDatasetsMap.set(chartData.datasets.length - 1, {});
            });
            // horizontal markers
            inputMarkerLines.horizontal.forEach(marker => {
                chartData.datasets.push({
                    label: marker.label || '',
                    data: [
                        { x: bounds.minX, y: marker.coordinate },
                        { x: bounds.maxX, y: marker.coordinate },
                    ],
                    fill: false,
                    showLine: true,
                    borderWidth: 1.5,
                    borderColor: marker.color.clone().alpha(1).rgbString(),
                    backgroundColor: marker.color.clone().alpha(1).rgbString(),
                    pointRadius: 0,
                    pointHitRadius: 0,
                    borderDash: BORDER_DASH_MARKER[marker.lineStyle || 'dashed'],
                    hidden: false,
                });
                this.parentDatasetsMap.set(chartData.datasets.length - 1, {});
            });
            // vertical markers
            inputMarkerLines.vertical.forEach(marker => {
                chartData.datasets.push({
                    label: marker.label || '',
                    data: [
                        { x: marker.coordinate, y: bounds.minY },
                        { x: marker.coordinate, y: bounds.maxY },
                    ],
                    fill: false,
                    showLine: true,
                    borderWidth: 1.5,
                    borderColor: marker.color.clone().alpha(1).rgbString(),
                    backgroundColor: marker.color.clone().alpha(1).rgbString(),
                    pointRadius: 0,
                    pointHitRadius: 0,
                    borderDash: BORDER_DASH_MARKER[marker.lineStyle || 'dashed'],
                });
                this.parentDatasetsMap.set(chartData.datasets.length - 1, {});
            });
            return chartData;
        }), shareReplay(1));
        /** Whether to show legend */
        this._showLegend$ = this._inputElementsVisibility$.pipe(map$1(inputElementsVisibility => inputElementsVisibility.legend !== false));
        /** The Chart options */
        this._options$ = combineLatest([
            this._inputData$,
            this._inputAxes$,
            this._inputElementsVisibility$,
            this._bounds$,
            this._terms$,
        ]).pipe(filter(([inputDatasets, inputAxes, inputElementsVisibility, bounds, terms]) => Boolean(inputDatasets && inputAxes && inputElementsVisibility && bounds && terms)), map$1(([inputDatasets, inputAxes, inputElementsVisibility, bounds, terms]) => ({
            maintainAspectRatio: false,
            responsive: true,
            resizeDelay: 10,
            scales: {
                x: {
                    type: inputAxes.x.type === 'time' ? 'time' : 'linear',
                    title: {
                        display: !!inputAxes.x.label,
                        text: inputAxes.x.label,
                    },
                    time: inputAxes.x.type === 'time' ?
                        {
                            displayFormats: {
                                minute: TIMESTAMP_FORMAT_READABLE,
                                hour: TIMESTAMP_FORMAT_READABLE,
                                day: DATE_FORMAT_READABLE,
                                week: DATE_FORMAT_READABLE,
                                month: 'MMM YYYY',
                                quarter: '[Q]Q YYYY',
                                year: 'YYYY',
                            },
                            minUnit: 'minute',
                        } :
                        undefined,
                    display: inputElementsVisibility.xAxis !== false,
                    grid: {
                        display: inputElementsVisibility.xGrid !== false,
                    },
                    min: bounds.minX,
                    max: bounds.maxX,
                    ticks: {
                        includeBounds: false,
                        callback: LineChartComponent.renderXTicks(inputAxes.x.unit),
                    },
                },
                y: {
                    offset: false,
                    title: {
                        display: !!inputAxes.y.label,
                        text: inputAxes.y.label,
                    },
                    ...inputAxes.y.ticks ?
                        {
                            ticks: {
                                callback: LineChartComponent.renderYTicks(inputAxes.y.ticks, inputAxes.y.unit),
                                color: (context) => LineChartComponent.closestScaleTick(context.tick.value, inputAxes.y.ticks).color,
                            },
                            afterBuildTicks: (axis) => axis.ticks = inputAxes.y.ticks.map(t => ({ value: t.value })),
                        } :
                        {
                            ticks: {
                                callback: LineChartComponent.renderYTicks(inputAxes.y.ticks, inputAxes.y.unit),
                                includeBounds: false,
                            },
                        },
                    display: inputElementsVisibility.yAxis !== false,
                    grid: {
                        display: inputElementsVisibility.yGrid !== false,
                    },
                    min: bounds.minY,
                    max: bounds.maxY,
                },
            },
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        boxWidth: 5,
                        filter: (item) => this.parentDatasetsMap.get(item.datasetIndex) !== undefined,
                    },
                    onClick: this.onClickLegendItem,
                },
                tooltip: {
                    ...TOOLTIP_STYLES,
                    callbacks: LineChartComponent.tooltipFunctions(inputAxes.y.ticks, this.parentDatasetsMap, terms, inputAxes.x.unit, inputAxes.y.unit),
                    mode: 'x',
                    intersect: true,
                    displayColors: inputDatasets.length !== 1,
                    // Show tooltip only for 1 x-element at the time
                    filter: (tooltipItem, _index, allTooltipItems) => tooltipItem.raw.x === allTooltipItems[0].raw.x,
                },
            },
        })));
        /**
         * On click handler for a legend item.
         * A click on a parent dataset affects also its children. This is used to group the limits to the data line.
         * Chart.js docs: https://www.chartjs.org/docs/latest/configuration/legend.html#custom-on-click-actions
         */
        this.onClickLegendItem = (_event, legendItem, legend) => {
            const parentIndex = legendItem.datasetIndex;
            const childIndices = this.parentDatasetsMap.get(parentIndex);
            // Legend clicks can only take effect on parent datasets
            if (childIndices === undefined)
                return;
            const chartInterface = legend.chart;
            const flatDatasetInterfaces = [parentIndex, ...Object.values(childIndices)].map(datasetIndex => chartInterface.getDatasetMeta(datasetIndex));
            const newHidden = chartInterface.isDatasetVisible(parentIndex);
            flatDatasetInterfaces.forEach(datasetInterface => datasetInterface.hidden = newHidden);
            chartInterface.update();
        };
        /** Gradient caching */
        this._gradientCache = {};
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LineChartComponent, deps: [{ token: ColorsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: LineChartComponent, selector: "apos-line-chart", inputs: { data: "data", axes: "axes", elementsVisibility: "elementsVisibility", markerLines: "markerLines" }, ngImport: i0, template: "<div class=\"position-relative w-100 h-100\">\n  <canvas\n    *ngIf=\"\n      (_data$ | async) !== null &&\n      (_options$ | async) !== null &&\n      (_showLegend$ | async) !== null\n    \"\n    baseChart\n    [type]=\"'scatter'\"\n    [data]=\"_data$ | async\"\n    [options]=\"_options$ | async\"\n    [legend]=\"_showLegend$ | async\"\n    aria-label=\"Line Chart\"\n    role=\"img\"\n  ></canvas>\n</div>\n", styles: [":host{display:block;height:100%;width:100%}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.BaseChartDirective, selector: "canvas[baseChart]", inputs: ["type", "legend", "data", "options", "plugins", "labels", "datasets"], outputs: ["chartClick", "chartHover"], exportAs: ["base-chart"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LineChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-line-chart', template: "<div class=\"position-relative w-100 h-100\">\n  <canvas\n    *ngIf=\"\n      (_data$ | async) !== null &&\n      (_options$ | async) !== null &&\n      (_showLegend$ | async) !== null\n    \"\n    baseChart\n    [type]=\"'scatter'\"\n    [data]=\"_data$ | async\"\n    [options]=\"_options$ | async\"\n    [legend]=\"_showLegend$ | async\"\n    aria-label=\"Line Chart\"\n    role=\"img\"\n  ></canvas>\n</div>\n", styles: [":host{display:block;height:100%;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: ColorsService }]; }, propDecorators: { data: [{
                type: Input
            }], axes: [{
                type: Input
            }], elementsVisibility: [{
                type: Input
            }], markerLines: [{
                type: Input
            }] } });

class AposLineChartsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposLineChartsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposLineChartsModule, declarations: [LineChartComponent], imports: [CommonModule,
            NgChartsModule], exports: [LineChartComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposLineChartsModule, imports: [CommonModule,
            NgChartsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposLineChartsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        LineChartComponent,
                    ],
                    imports: [
                        CommonModule,
                        NgChartsModule,
                    ],
                    exports: [
                        LineChartComponent,
                    ],
                }]
        }] });

// @dynamic
class PieChartComponent {
    /**
     * Data (input setter)
     */
    set data(data) { this._inputData$.next(data); }
    /**
     * Elements visiblity (input setter)
     * The visibility of the charts elements
     */
    set elementsVisibility(elementsVisibility) { this._inputElementsVisibility$.next(elementsVisibility); }
    /**
     * The unit, to be displayed in the tooltip (Input setter)
     */
    set unit(unit) { this._unit$.next(unit); }
    constructor(colorsService) {
        this.colorsService = colorsService;
        /** Data (input subject) */
        this._inputData$ = new ReplaySubject(1);
        /** Elements visibility (input subject) */
        this._inputElementsVisibility$ = new BehaviorSubject({});
        /** Unit (input subject) */
        this._unit$ = new BehaviorSubject('');
        /** The Chart data for chartjs */
        this._data$ = this._inputData$.pipe(filter((inputDatasets) => Boolean(inputDatasets)), map$1((inputDatasets) => {
            let colorIndex = 0;
            const mapped = inputDatasets
                .filter(d => Number.isFinite(d.data.value))
                .map(d => ({
                ...d,
                color: colorLib(d.color || this.colorsService.rotateColorSolid(colorIndex++)),
            }));
            return mapped;
        }), map$1((inputDatasets) => {
            const chartData = {
                datasets: [{
                        data: inputDatasets.map(d => d.data.value),
                        backgroundColor: inputDatasets.map(d => d.color.clone().rgbString()),
                        hoverBackgroundColor: inputDatasets.map(d => d.color.clone().rgbString()),
                        borderColor: 'white',
                        hoverBorderColor: 'white',
                        hoverOffset: 10,
                    }],
                labels: inputDatasets.map(d => d.label || ''),
            };
            return chartData;
        }), shareReplay(1));
        /** Whether to show legend */
        this._showLegend$ = this._inputElementsVisibility$.pipe(map$1(inputElementsVisibility => inputElementsVisibility.legend !== false));
        /** The Chart options */
        this._options$ = of({
            maintainAspectRatio: false,
            responsive: true,
            resizeDelay: 10,
            layout: {
                padding: 10,
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: ([item]) => {
                            return item.label;
                        },
                        label: (item) => {
                            let valueString = Number(item.raw.toPrecision(DEFAULT_SIGNIFICANT_DIGITS)).toString();
                            if (this._unit$.getValue())
                                valueString = `${valueString} ${this._unit$.getValue()}`;
                            return valueString;
                        },
                    },
                },
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PieChartComponent, deps: [{ token: ColorsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: PieChartComponent, selector: "apos-pie-chart", inputs: { data: "data", elementsVisibility: "elementsVisibility", unit: "unit" }, ngImport: i0, template: "<div class=\"position-relative w-100 h-100\">\n  <canvas\n    *ngIf=\"\n      (_data$ | async) !== null &&\n      (_options$ | async) !== null &&\n      (_showLegend$ | async) !== null\n    \"\n    baseChart\n    [type]=\"'pie'\"\n    [data]=\"_data$ | async\"\n    [options]=\"_options$ | async\"\n    [legend]=\"_showLegend$ | async\"\n    aria-label=\"Pie Chart\"\n    role=\"img\"\n  ></canvas>\n</div>\n", styles: [":host{display:block;height:100%;width:100%}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.BaseChartDirective, selector: "canvas[baseChart]", inputs: ["type", "legend", "data", "options", "plugins", "labels", "datasets"], outputs: ["chartClick", "chartHover"], exportAs: ["base-chart"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PieChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-pie-chart', template: "<div class=\"position-relative w-100 h-100\">\n  <canvas\n    *ngIf=\"\n      (_data$ | async) !== null &&\n      (_options$ | async) !== null &&\n      (_showLegend$ | async) !== null\n    \"\n    baseChart\n    [type]=\"'pie'\"\n    [data]=\"_data$ | async\"\n    [options]=\"_options$ | async\"\n    [legend]=\"_showLegend$ | async\"\n    aria-label=\"Pie Chart\"\n    role=\"img\"\n  ></canvas>\n</div>\n", styles: [":host{display:block;height:100%;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: ColorsService }]; }, propDecorators: { data: [{
                type: Input
            }], elementsVisibility: [{
                type: Input
            }], unit: [{
                type: Input
            }] } });

// export * from './pie-chart/pie-chart.type';

class AposPieChartsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposPieChartsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposPieChartsModule, declarations: [PieChartComponent], imports: [CommonModule,
            NgChartsModule], exports: [PieChartComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposPieChartsModule, imports: [CommonModule,
            NgChartsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposPieChartsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PieChartComponent,
                    ],
                    imports: [
                        CommonModule,
                        NgChartsModule,
                    ],
                    exports: [
                        PieChartComponent,
                    ],
                }]
        }] });

class LibTestComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LibTestComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: LibTestComponent, selector: "apos-lib-test", ngImport: i0, template: "<p>lib-test works!</p>\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LibTestComponent, decorators: [{
            type: Component,
            args: [{ selector: 'apos-lib-test', template: "<p>lib-test works!</p>\n" }]
        }] });

class SafeHtmlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Transform the url to a safe url
     */
    /* eslint class-methods-use-this: "off"*/
    transform(url) {
        return this.sanitizer.bypassSecurityTrustHtml(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeHtmlPipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafeHtmlPipe, name: "safeHtml" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safeHtml',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class SafeUrlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Transform the url to a safe url
     */
    /* eslint class-methods-use-this: "off"*/
    transform(url) {
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeUrlPipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafeUrlPipe, name: "safeUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeUrlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safeUrl',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class JsonstringPipe {
    transform(value) {
        return JSON.parse(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: JsonstringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: JsonstringPipe, name: "jsonstring" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: JsonstringPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'jsonstring',
                }]
        }] });

class SignificantDigitsPipe {
    transform(value, significantDigits = 3) {
        if (typeof value !== "number" || !Number.isFinite(value))
            return "NaN";
        return Number(value.toPrecision(significantDigits)).toString();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SignificantDigitsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SignificantDigitsPipe, name: "significantDigits" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SignificantDigitsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'significantDigits',
                }]
        }] });

class RedirectUriPipe {
    /**
     * Add redirect_uri parameter to a url
     */
    /* eslint class-methods-use-this: "off"*/
    transform(url, redirectUri, paramName = 'redirect_uri') {
        let separator = '';
        if (url.includes('?') && url.split('?')[url.split('?').length - 1].length > 0)
            separator = '&';
        else if (!url.includes('?'))
            separator = '?';
        return `${url}${separator}${paramName}=${encodeURIComponent(redirectUri)}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RedirectUriPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: RedirectUriPipe, name: "redirectUri" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RedirectUriPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'redirectUri',
                }]
        }] });

class SafeResourceUrlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Transform the url to a safe url
     */
    /* eslint class-methods-use-this: "off"*/
    transform(url) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeResourceUrlPipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafeResourceUrlPipe, name: "safeResourceUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeResourceUrlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safeResourceUrl',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class SafeScriptPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Transform the url to a safe url
     */
    /* eslint class-methods-use-this: "off"*/
    transform(url) {
        return this.sanitizer.bypassSecurityTrustScript(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeScriptPipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafeScriptPipe, name: "safeScript" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeScriptPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safeScript',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class SafeStylePipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Transform the url to a safe url
     */
    /* eslint class-methods-use-this: "off"*/
    transform(url) {
        return this.sanitizer.bypassSecurityTrustStyle(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeStylePipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafeStylePipe, name: "safeStyle" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafeStylePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safeStyle',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class AposPipesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AposPipesModule, declarations: [JsonstringPipe,
            RedirectUriPipe,
            SafeHtmlPipe,
            SafeResourceUrlPipe,
            SafeScriptPipe,
            SafeStylePipe,
            SafeUrlPipe,
            SignificantDigitsPipe], imports: [CommonModule], exports: [JsonstringPipe,
            RedirectUriPipe,
            SafeHtmlPipe,
            SafeResourceUrlPipe,
            SafeScriptPipe,
            SafeStylePipe,
            SafeUrlPipe,
            SignificantDigitsPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposPipesModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AposPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        JsonstringPipe,
                        RedirectUriPipe,
                        SafeHtmlPipe,
                        SafeResourceUrlPipe,
                        SafeScriptPipe,
                        SafeStylePipe,
                        SafeUrlPipe,
                        SignificantDigitsPipe,
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        JsonstringPipe,
                        RedirectUriPipe,
                        SafeHtmlPipe,
                        SafeResourceUrlPipe,
                        SafeScriptPipe,
                        SafeStylePipe,
                        SafeUrlPipe,
                        SignificantDigitsPipe,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AposBarChartsModule, AposComponentsModule, AposFormsModule, AposLineChartsModule, AposPieChartsModule, AposPipesModule, AposTooltipsModule, ArrayNotEmptyValidator, ArrayPropertyUniqueValidator, AutoUnsubscribe, BarChartComponent, CronEditorComponent, DateMaxValidator, DateMinValidator, DateValidator, ExpandingMenuBarComponent, ExpandingMenuBarItemDirective, FileEndingValidator, JsonstringPipe, LibTestComponent, LineChartComponent, MinCheckedValidator, PieChartComponent, RedirectUriPipe, SafeHtmlPipe, SafeResourceUrlPipe, SafeScriptPipe, SafeStylePipe, SafeUrlPipe, SignificantDigitsPipe, SparklesComponent, TooltipDirective, getComponentProp };
